import type { CampaignMode, ParallelMode, SalesStatus, SipMode } from '@autodialer/shared';

import { PREVIEW_MODE } from '../preview/preview-mode';
import { ApiError, api } from './client';

/** Zugriff auf Kampagnen, Datensätze, Import und Export. */

export function withQuery(path: string, params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  campaignMode: CampaignMode;
  parallelCalls: number;
  parallelMode: ParallelMode;
  retryCount24h: number;
  inboundEnabled: boolean;
  queueEnabled: boolean;
  queueMaxWaitSeconds: number;
  recordingDefault: boolean;
  mailboxDetectionEnabled: boolean;
  defaultCountryCode: string;
  sipMode: SipMode;
  archivedAt: string | null;
  createdAt: string;
  /** Name der hochgeladenen Wartemusik - nur zur Anzeige. */
  queueMusicFilename?: string | null;
}

export interface CampaignField {
  id: string;
  fieldName: string;
  sortOrder: number;
  requiredBeforeCompletion: boolean;
}

export interface CampaignMember {
  userId: string;
  username: string | null;
  email: string | null;
  isAdmin: boolean;
  isActive: boolean;
  assignedContacts: number;
}

export interface Contact {
  id: string;
  publicId: string;
  phone: string;
  firstName: string | null;
  lastName: string | null;
  note: string | null;
  salesStatus: SalesStatus;
  callbackDate: string | null;
  assignedUserId: string | null;
  archived: boolean;
  fields: Record<string, string>;
  duplicateOf?: { id: string; publicId: string };
}

export interface ContactFilter {
  query?: string;
  salesStatus?: string;
  assignedUserId?: string;
  callbackFrom?: string;
  callbackTo?: string;
  archived?: 'active' | 'archived' | 'all';
  limit?: number;
  offset?: number;
}

export interface ModeChangePreview {
  currentMode: CampaignMode;
  targetMode: CampaignMode;
  affectedContacts: number;
  assignedCallers: number;
  possible: boolean;
  blockedReason?: string;
}

export interface ImportMapping {
  phoneColumn: string;
  firstNameColumn?: string | null;
  lastNameColumn?: string | null;
  publicIdColumn?: string | null;
  noteColumn?: string | null;
  statusColumn?: string | null;
  callbackDateColumn?: string | null;
}

export interface ImportAnalysis {
  importId: string;
  delimiter: string;
  columns: string[];
  sampleRows: Record<string, string>[];
  totalRows: number;
  validPhones: number;
  invalidPhones: number;
  duplicates: number;
  rowsWithPublicId: number;
  suggestedMapping: ImportMapping;
}

export interface ImportReport {
  totalRows: number;
  created: number;
  updated: number;
  skippedDuplicates: number;
  skippedInvalid: number;
  errors: { line: number; reason: string }[];
  createdFields: string[];
}

const campaignPath = (id: string): string => '/campaigns/' + id;

export const campaignsApi = {
  list: (includeArchived = false) =>
    api.get<{ campaigns: Campaign[] }>(withQuery('/campaigns', { includeArchived: includeArchived ? 'true' : '' })),
  listMine: () => api.get<{ campaigns: Campaign[] }>('/campaigns/mine'),
  get: (id: string) => api.get<{ campaign: Campaign }>(campaignPath(id)),
  create: (name: string, description?: string) =>
    api.post<{ campaign: Campaign }>('/campaigns', { name, description: description ?? null }),
  update: (id: string, changes: Record<string, unknown>) =>
    api.patch<{ campaign: Campaign }>(campaignPath(id), changes),
  setActive: (id: string, isActive: boolean) =>
    api.patch<{ campaign: Campaign }>(campaignPath(id) + '/active', { isActive }),
  previewMode: (id: string, mode: CampaignMode) =>
    api.get<{ preview: ModeChangePreview }>(withQuery(campaignPath(id) + '/mode-preview', { mode })),
  changeMode: (id: string, mode: CampaignMode) =>
    api.post<{ result: ModeChangePreview }>(campaignPath(id) + '/mode', { mode }),
  archive: (id: string) => api.post<{ ok: true }>(campaignPath(id) + '/archive'),
  restore: (id: string) => api.post<{ ok: true }>(campaignPath(id) + '/restore'),
  remove: (id: string) => api.delete<{ ok: true }>(campaignPath(id)),

  listMembers: (id: string) => api.get<{ members: CampaignMember[] }>(campaignPath(id) + '/members'),
  addMember: (id: string, userId: string) =>
    api.post<{ members: CampaignMember[] }>(campaignPath(id) + '/members', { userId }),
  removeMember: (id: string, userId: string) =>
    api.delete<{ unassignedContacts: number }>(campaignPath(id) + '/members/' + userId),

  listFields: (id: string) => api.get<{ fields: CampaignField[] }>(campaignPath(id) + '/fields'),
  createField: (id: string, fieldName: string, requiredBeforeCompletion = false) =>
    api.post<{ field: CampaignField }>(campaignPath(id) + '/fields', { fieldName, requiredBeforeCompletion }),
  updateField: (id: string, fieldId: string, changes: Record<string, unknown>) =>
    api.patch<{ field: CampaignField }>(campaignPath(id) + '/fields/' + fieldId, changes),
  removeField: (id: string, fieldId: string) => api.delete<{ ok: true }>(campaignPath(id) + '/fields/' + fieldId),
};

export const contactsApi = {
  list: (campaignId: string, filter: ContactFilter = {}) =>
    api.get<{ contacts: Contact[]; total: number; fields: CampaignField[] }>(
      withQuery(campaignPath(campaignId) + '/contacts', filter as Record<string, string | number | undefined>),
    ),
  get: (campaignId: string, contactId: string) =>
    api.get<{ contact: Contact }>(campaignPath(campaignId) + '/contacts/' + contactId),
  create: (campaignId: string, input: Record<string, unknown>) =>
    api.post<{ contact: Contact }>(campaignPath(campaignId) + '/contacts', input),
  update: (campaignId: string, contactId: string, input: Record<string, unknown>) =>
    api.patch<{ contact: Contact }>(campaignPath(campaignId) + '/contacts/' + contactId, input),
  archive: (campaignId: string, contactId: string) =>
    api.post<{ ok: true }>(campaignPath(campaignId) + '/contacts/' + contactId + '/archive'),
  restore: (campaignId: string, contactId: string) =>
    api.post<{ ok: true }>(campaignPath(campaignId) + '/contacts/' + contactId + '/restore'),
  remove: (campaignId: string, contactId: string) =>
    api.delete<{ ok: true }>(campaignPath(campaignId) + '/contacts/' + contactId),
};

export const importApi = {
  /** Schritt 1: Datei hochladen. Es wird noch nichts gespeichert. */
  upload: async (campaignId: string, file: File): Promise<ImportAnalysis> => {
    if (PREVIEW_MODE) {
      const { previewUpload } = await import('../preview/preview-api');
      return previewUpload(campaignId, file);
    }

    const form = new FormData();
    form.append('file', file);

    const response = await fetch('/api' + campaignPath(campaignId) + '/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const payload = (await response.json().catch(() => null)) as
      | { analysis?: ImportAnalysis; code?: string; details?: Record<string, unknown> }
      | null;

    if (!response.ok || !payload?.analysis) {
      throw new ApiError(payload?.code ?? 'unknown', response.status, payload?.details);
    }
    return payload.analysis;
  },
  analyze: (campaignId: string, importId: string, mapping: ImportMapping) =>
    api.post<{ analysis: ImportAnalysis }>(campaignPath(campaignId) + '/import/' + importId + '/analyze', {
      mapping,
    }),
  commit: (campaignId: string, importId: string, mapping: ImportMapping, duplicateStrategy: 'skip' | 'overwrite') =>
    api.post<{ report: ImportReport }>(campaignPath(campaignId) + '/import/' + importId + '/commit', {
      mapping,
      duplicateStrategy,
    }),
  /** Der Export läuft als normaler Download über einen Link. */
  exportUrl: (campaignId: string, filter: ContactFilter = {}) =>
    '/api' + withQuery(campaignPath(campaignId) + '/export', filter as Record<string, string | number | undefined>),
};

/**
 * Wartemusik einer Kampagne - eine Datei, mehr nicht.
 * Der Dateiname aus dem Browser wird serverseitig nicht als Pfad verwendet.
 */
export const queueMusicApi = {
  upload: async (campaignId: string, file: File): Promise<{ filename: string }> => {
    if (PREVIEW_MODE) {
      const { previewQueueMusic } = await import('../preview/preview-api');
      return previewQueueMusic(campaignId, file);
    }

    const form = new FormData();
    form.append('file', file);

    const response = await fetch('/api' + campaignPath(campaignId) + '/queue-music', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const payload = (await response.json().catch(() => null)) as
      | { filename?: string; code?: string; details?: Record<string, unknown> }
      | null;

    if (!response.ok || !payload?.filename) {
      throw new ApiError(payload?.code ?? 'unknown', response.status, payload?.details);
    }
    return { filename: payload.filename };
  },
  remove: (campaignId: string) => api.delete<{ ok: true }>(campaignPath(campaignId) + '/queue-music'),
};
